import React from 'react';

import { Icon, Skeleton, Stack, Text } from '@eluve/components';

import type { FhirSectionStatus } from './section-state';

/**
 * Per-section degradation boundary for access-gated FHIR reads: children on
 * data, standardized placeholders otherwise. Every section of the patient
 * profile wraps itself in one so a compartment-scoped member sees a
 * "restricted" section — never a crashed page, and never "no data" when the
 * truth is "no access".
 *
 * Lives here with the query-state hooks (not in the profile feature lib)
 * because the section libs that consume both sit under libs/frontend/fhir/,
 * and @eluve/feature-patients is a dependency of this lib — same placement
 * rationale as LoadMoreRow.
 */
export const SectionBoundary: React.FC<{
  status: FhirSectionStatus;
  /** Empty-state copy, e.g. "No conditions recorded." */
  emptyText: string;
  children: React.ReactNode;
}> = ({ status, emptyText, children }) => {
  switch (status) {
    case 'data':
      return <>{children}</>;
    case 'loading':
      return (
        <Stack gap={2} className="py-4" data-testid="section-loading">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </Stack>
      );
    case 'empty':
      return (
        <Text
          size="sm"
          color="tertiary"
          weight="medium"
          className="block py-4"
          data-testid="section-empty"
        >
          {emptyText}
        </Text>
      );
    case 'forbidden':
      return (
        <Stack
          direction="horizontal"
          gap={2}
          className="items-center py-4"
          data-testid="section-forbidden"
        >
          <Icon name="Lock" size="xs" className="text-text-tertiary" />
          <Text size="sm" color="tertiary" weight="medium">
            You don't have access to this section.
          </Text>
        </Stack>
      );
    case 'error':
      return (
        <Text
          size="sm"
          color="tertiary"
          weight="medium"
          className="block py-4"
          data-testid="section-error"
        >
          This section couldn't be loaded.
        </Text>
      );
  }
};
